import React from 'react';
import { ImageCropper } from '@eventbrite/eds-image-cropper';
import {
    calculateWidthToPreventAncestorScroll,
    pickPropsByTypes,
} from '../utils';

/**
 * This component is needed to handle a complex layout concern.
 * The cropper is built to take up the maximum amount of horizontal space possible.
 * This allows us to use it with our grid to manage responsiveness and constrain size.
 * Unfortunately, with portrait images (especially if they are very tall) this
 * causes it to overflow the vertical height available and a scrollbar appears.
 * This isn't ideal and makes it hard to use.
 * Ideally, we'd use CSS `object-fit: contain` to achieve the desired behavior but
 * due to the complex nature of the croppers layout and functional requirements,
 * this is not feasible.
 * This component introduces manual width handling based on the available height to
 * mimic the behavior of `object-fit: contain` in a way that works with the cropper.
 *
 * Please don't reuse this methodology without consulting with Frontend-foundry first
 * to seek a more optimized, standardized, and universal solution.
 */
export default class ConstrainedHeightCropper extends React.Component {
    static propTypes = {
        ...ImageCropper.propTypes,
    };

    state = {
        width: undefined,
    };

    componentDidUpdate() {
        this._explicitlySetWidth();
    }

    _setRef(node) {
        this._container = node;
    }

    _explicitlySetWidth() {
        const { width } = this.state;
        const newWidth = calculateWidthToPreventAncestorScroll(this._container);

        if (newWidth && newWidth !== width) {
            this.setState({
                width: newWidth,
            });
        }
    }

    render() {
        const cropperProps = pickPropsByTypes(
            this.props,
            ImageCropper.propTypes,
        );
        const { width } = this.state;
        let styles;

        if (width) {
            styles = { width };
        }

        return (
            <div
                ref={this._setRef.bind(this)}
                style={styles}
                className="cropper-image-center"
            >
                <ImageCropper {...cropperProps} />
            </div>
        );
    }
}
